import { FetchBody, FetchMethod, InterceptorFunctions } from "../api/fetch/fetch.types";

const isSameOrigin = (url: string | URL) => {
  const a = document.createElement('a');
  a.href = url instanceof URL ? url.toString() : url;
  return a.hostname == window.location.hostname;
}

interface ExtendedXHR extends XMLHttpRequest {
  interceptionData?: {
    url?: string | URL,
    method?: string,
    contentType?: string,
  }
}

const getBody = (ogBody: Document | XMLHttpRequestBodyInit | null | undefined, contentType?: string): FetchBody => {
  let body: FetchBody;
  if (!ogBody) return '';
  if (contentType == 'application/json' && typeof ogBody === 'string') {
    try {
      body = JSON.parse(ogBody);
    } catch (err) {
      console.error(err);
      body = {};
    }
  } else if (ogBody instanceof FormData) {
    body = ogBody;
  } else if (typeof ogBody === 'string') {
    body = ogBody;
  } else {
    body = ogBody + '';
  }
  return body;
}

const generateFakeResponse = (status: number, statusText: string, res: string, contentType?: string | null): Response => {
  const resInit: ResponseInit = {
    status,
    statusText
  }
  if (contentType) resInit.headers = { 'Content-Type': contentType };
  return new Response(res, resInit)
}

const xhrOpenProxy = (target: XMLHttpRequest['open'], thisArg: ExtendedXHR, args: Parameters<XMLHttpRequest['open']>) => {
  try {
    const [method, url] = args;
    thisArg.interceptionData = {
      ...thisArg.interceptionData,
      method,
      url
    };
  } catch (err) {
    console.error(err);
  }
  return target.apply(thisArg, args);
};

const xhrSetRequestHeaderProxy = (target: XMLHttpRequest['setRequestHeader'], thisArg: ExtendedXHR, args: Parameters<XMLHttpRequest['setRequestHeader']>) => {
  try {
    const [name, value] = args;
    if (name.match(/content\-type/i)) {
      thisArg.interceptionData = {
        ...thisArg.interceptionData,
        contentType: value
      };
    }
  } catch (err) {
    console.error(err);
  }

  return target.apply(thisArg, args);
};

const xhrSendProxy = (interceptorFunctions: InterceptorFunctions) => {
  return (target: XMLHttpRequest['send'], thisArg: ExtendedXHR, args: Parameters<XMLHttpRequest['send']>) => {
    try {
      if (thisArg.interceptionData && thisArg.interceptionData.url && isSameOrigin(thisArg.interceptionData.url)) {
        const body = getBody(args[0]);
        const url = thisArg.interceptionData.url instanceof URL ? thisArg.interceptionData.url.toString() : thisArg.interceptionData.url;
        const method = thisArg.interceptionData.method?.toUpperCase() as FetchMethod ?? 'GET';
        interceptorFunctions.beforeFetch(url, method, body);
        thisArg.addEventListener('load', function () {
          const xhrResponse = this.responseText;
          const response = generateFakeResponse(this.status, this.statusText, xhrResponse, this.getResponseHeader('Content-Type'));
          interceptorFunctions.afterFetch(url, method, body, response);
        });
      }
    } catch (err) {
      console.error(err);
    }

    return target.apply(thisArg, args);
  };
}

export const createInterceptableAjaxProxy = (XMLHttpRequest: typeof window.XMLHttpRequest, interceptorFunctions: InterceptorFunctions): void => {
  XMLHttpRequest.prototype.open = new Proxy<XMLHttpRequest['open']>(XMLHttpRequest.prototype.open, {
    apply: xhrOpenProxy
  });
  XMLHttpRequest.prototype.setRequestHeader = new Proxy<XMLHttpRequest['setRequestHeader']>(XMLHttpRequest.prototype.setRequestHeader, {
    apply: xhrSetRequestHeaderProxy
  });
  XMLHttpRequest.prototype.send = new Proxy<XMLHttpRequest['send']>(XMLHttpRequest.prototype.send, {
    apply: xhrSendProxy(interceptorFunctions)
  });
}
